import React, { useState, useRef, useCallback, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Sparkles, Loader2, Palette, Wand2, LogIn, Camera } from "lucide-react";
import confetti from "canvas-confetti";
import { playMagicReveal, warmUpAudio } from "../utils/sounds";
import { MagicHeader, MagicLogoWatermark } from "../components/magic/MagicHeader";
import { MagicRevealAnimation, MagicResultView } from "../components/magic/MagicResult";
import { MagicTemplateGallery } from "../components/magic/MagicTemplateGallery";
import { MagicPaywall } from "../components/magic/MagicPaywall";
import MagicUploader from "../components/magic/MagicUploader";
import MagicCustomAddons from "../components/magic/MagicCustomAddons";
import { PACKAGES, OCCASIONS, COLOR_PRESETS, ADDON_CATEGORIES } from "../components/magic/MagicConstants";
import useMagicSlider from "../hooks/useMagicSlider";
import { downloadMagicImage, shareMagicImage } from "../utils/magicShare";
import EarlyBirdStrip from "../components/EarlyBirdStrip";

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { getCustomerAuthHeaders } from "../utils/customerAuth";

const SESSION_KEY = "cs_tok"; // legacy — pre-React inline bootstrap only
const getAuthHeaders = () => getCustomerAuthHeaders();

export default function DoozyGoMagic() {
  const navigate = useNavigate();
  const { jobId: publicJobId } = useParams();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [selectedPkg, setSelectedPkg] = useState("Classic");
  const [selectedOccasion, setSelectedOccasion] = useState("Birthday");
  const [selectedColors, setSelectedColors] = useState("");
  const [customColors, setCustomColors] = useState("");
  const [extras, setExtras] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [dedupNotice, setDedupNotice] = useState(false);
  const [genStatus, setGenStatus] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const [revealPhase, setRevealPhase] = useState(null);
  const [usage, setUsage] = useState({ free_used: 0, free_limit: 2, free_remaining: 2, has_pass: false, pass_name: "" });
  const [showPaywall, setShowPaywall] = useState(false);
  const [teamMode, setTeamMode] = useState(() => sessionStorage.getItem("dg_team") === "1");
  const [teamCode, setTeamCode] = useState(() => sessionStorage.getItem("dg_team_code") || "");
  const [paywallTeamOpen, setPaywallTeamOpen] = useState(false);
  const [paywallTeamCode, setPaywallTeamCode] = useState("");
  const [showTeamInput, setShowTeamInput] = useState(false);
  const [buyingPlan, setBuyingPlan] = useState("");
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(() => {
    // Persist the Hide/Unhide choice across reloads. User asked for an explicit
    // Unhide option — see docx "Add an option to unhide AI-generated images".
    try { return localStorage.getItem("dg_magic_templates_hidden") !== "1"; }
    catch { return true; }
  });
  const toggleTemplates = (next) => {
    setShowTemplates(next);
    try { localStorage.setItem("dg_magic_templates_hidden", next ? "0" : "1"); }
    catch { /* storage blocked — session-only toggle still works */ }
  };
  const [customItems, setCustomItems] = useState(new Set());

  // Before/After slider state — extracted to useMagicSlider hook
  const {
    sliderPos,
    setSliderPos,
    sliderContainerRef,
    handleSliderMouseDown,
    handleSliderTouchStart,
  } = useMagicSlider(50);


  // Check login status on mount + prefill last package/occasion preference
  useEffect(() => {
    const headers = getAuthHeaders();
    if (headers.Authorization) {
      fetch(`${API_URL}/api/customer-auth/me`, { credentials: "include", headers })
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(d => {
          setIsLoggedIn(true);
          setCustomerName(d.name || d.email || "");
          // Prefill last-used package/occasion so returning customers don't re-pick every time.
          if (d.last_package && PACKAGES.some(p => p.name === d.last_package)) setSelectedPkg(d.last_package);
          if (d.last_occasion && OCCASIONS.some(o => o.name === d.last_occasion)) setSelectedOccasion(d.last_occasion);
        })
        .catch(() => setIsLoggedIn(false));
    }
  }, []);

  // Fetch usage and templates on mount
  useEffect(() => {
    fetch(`${API_URL}/api/magic/usage`, { credentials: "include" }).then(r => r.json()).then(setUsage).catch(() => {});
    const fetchTemplates = () =>
      fetch(`${API_URL}/api/magic/templates`).then(r => r.json()).then(d => {
        const tpls = d.templates || [];
        setTemplates(tpls);
        return tpls.length;
      }).catch(() => 0);
    fetchTemplates().then(count => {
      if (count === 0) {
        const interval = setInterval(() => {
          fetchTemplates().then(c => { if (c > 0) clearInterval(interval); });
        }, 15000);
        return () => clearInterval(interval);
      }
    });
  }, []);

  // Public shareable link: /magic/:jobId — auto-load the completed result into the result view
  useEffect(() => {
    if (!publicJobId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/magic/status/${publicJobId}`);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled || data.status !== "completed" || !data.image_base64) return;
        setResult({ ...data, is_ai_preview: data.is_ai_preview || !data.uploaded_image, is_shared_link: true });
        setSelectedPkg(data.package || "Classic");
        setSelectedOccasion(data.occasion || "Birthday");
        setRevealPhase("revealed");
        setShowTemplates(false);
      } catch (err) { console.debug('[Magic] Shared link load failed:', err?.message); }
    })();
    return () => { cancelled = true; };
  }, [publicJobId]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Please upload an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { setError("Image too large (max 10 MB)"); return; }
    setUploadedFile(file);
    setUploadedImage(URL.createObjectURL(file));
    setResult(null);
    setError("");
  };

  const handleGenerate = async () => {
    if (!uploadedFile) return;

    // First 2 generations are frictionless (no login required) — backend tracks by IP for anon users.
    // Backend returns 401 LOGIN_REQUIRED after the 2nd free gen → we show the login prompt then.

    warmUpAudio();
    if (!teamMode && usage.free_remaining <= 0 && !usage.has_pass && isLoggedIn) {
      // Logged-in user out of free + no pass → paywall directly
      setShowPaywall(true);
      return;
    }

    setGenerating(true);
    setError("");
    setResult(null);

    const formData = new FormData();
    formData.append("image", uploadedFile);
    formData.append("package", selectedPkg);
    formData.append("occasion", selectedOccasion);
    formData.append("colors", selectedColors || customColors);
    formData.append("extras", extras);
    if (teamMode) formData.append("team_code", teamCode || sessionStorage.getItem("dg_team_code") || "");
    if (selectedPkg === "Custom" && customItems.size > 0) {
      formData.append("custom_items", Array.from(customItems).join(","));
    }

    // Build headers with auth token
    const headers = getAuthHeaders();

    try {
      const submitRes = await fetch(`${API_URL}/api/magic/generate`, {
        method: "POST",
        body: formData,
        credentials: "include",
        headers,
      });
      if (!submitRes.ok) {
        const err = await submitRes.json().catch(() => ({}));
        if (submitRes.status === 402 || err.detail === "FREE_LIMIT_REACHED") { setShowPaywall(true); setGenerating(false); return; }
        if (submitRes.status === 401 || err.detail === "LOGIN_REQUIRED") { setShowLoginPrompt(true); setGenerating(false); return; }
        if (submitRes.status === 403 && err.detail === "INVALID_TEAM_CODE") {
          // Wrong team code — clear the bad code and force re-entry. Don't
          // silently fall back to the free tier (security: prevents bypass).
          setError("Invalid team code. Please check with the team and try again.");
          setTeamMode(false);
          setTeamCode("");
          sessionStorage.removeItem("dg_team");
          sessionStorage.removeItem("dg_team_code");
          setShowTeamInput(true);
          setGenerating(false);
          return;
        }
        if (submitRes.status === 503 && err.detail === "QUEUE_FULL") {
          setError("DoozyGo® Magic is at peak load right now. Please try again in a few seconds.");
          setGenerating(false);
          return;
        }
        throw new Error(err.detail || "Failed to start generation");
      }
      const submitData = await submitRes.json();
      const { job_id } = submitData;
      if (submitData.free_remaining !== undefined) {
        setUsage(prev => ({ ...prev, free_remaining: submitData.free_remaining, free_used: prev.free_limit - submitData.free_remaining }));
      }

      // ⚡ Dedup cache hit — server returned a completed job with inline image data.
      // Skip polling entirely. Show a tiny "reused" pill so team members know
      // why it was instant (and that the image is identical to a recent gen).
      if (submitData.status === "completed" && submitData.image_base64) {
        setResult(submitData);
        setSliderPos(50);
        setGenerating(false);
        setRevealPhase("ready");
        if (submitData.dedup_hit) {
          setDedupNotice(true);
          setTimeout(() => setDedupNotice(false), 4000);
        }
        playMagicReveal();
        const colors = ['#E83E8C', '#FF9F1C', '#FFD166', '#25D366', '#8b5cf6'];
        confetti({ particleCount: 60, spread: 90, origin: { y: 0.6 }, colors, gravity: 1.0 });
        setTimeout(() => setRevealPhase("revealed"), 800);
        return;
      }

      // ⏳ In-flight dedup — server returned an existing processing job (this happens when
      // the same photo+package is already generating). Just poll the existing job_id.
      if (submitData.inflight_dedup) {
        setGenStatus("Already generating — joining your existing request...");
      }

      let attempts = 0;
      const maxAttempts = 100;  // 100 × 3s = 5 min — matches backend stale-job timeout
      const startTs = Date.now();
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 3000));
        attempts++;
        try {
          const pollRes = await fetch(`${API_URL}/api/magic/status/${job_id}`);
          const pollData = await pollRes.json();
          // Show elapsed time so users can see progress instead of clicking again
          const elapsed = Math.round((Date.now() - startTs) / 1000);
          if (pollData.status === "queued") {
            // Surface the live queue depth so users see the line moving.
            const depth = pollData.queue_depth ?? 0;
            setGenStatus(depth > 0
              ? `Waiting in line · ${depth} ahead · ${elapsed}s`
              : `Starting in a moment… · ${elapsed}s`);
          } else {
            setGenStatus(`AI is working on your room — ${elapsed}s elapsed`);
          }
          if (pollData.status === "completed") {
            setResult(pollData);
            setSliderPos(50);
            setGenerating(false);
            setRevealPhase("preparing");
            setTimeout(() => {
              setRevealPhase("ready");
              playMagicReveal();
              const colors = ['#E83E8C', '#FF9F1C', '#FFD166', '#25D366', '#8b5cf6'];
              confetti({ particleCount: 80, spread: 90, origin: { y: 0.6 }, colors, gravity: 1.0 });
              setTimeout(() => {
                confetti({ particleCount: 40, angle: 60, spread: 50, origin: { x: 0.1, y: 0.5 }, colors });
                confetti({ particleCount: 40, angle: 120, spread: 50, origin: { x: 0.9, y: 0.5 }, colors });
              }, 300);
              setTimeout(() => setRevealPhase("revealed"), 1800);
            }, 2000);
            return;
          } else if (pollData.status === "failed") {
            throw new Error(pollData.error || "Generation failed");
          }
        } catch (pollErr) {
          if (pollErr.message && !pollErr.message.includes("fetch")) throw pollErr;
        }
      }
      throw new Error("Generation timed out. Please try again.");
    } catch (err) {
      const msg = err.message || "";
      if (!msg.includes("postMessage") && !msg.includes("cloned")) {
        setError(msg || "Something went wrong. Please try again.");
      }
    }
    setGenerating(false);
  };

  const handleDownload = () => downloadMagicImage(result);
  const handleShare = () => shareMagicImage(result);

  const handleReset = () => {
    setResult(null);
    setUploadedImage(null);
    setUploadedFile(null);
    setError("");
    setRevealPhase(null);
    setExtras("");
    setShowTemplates(true);
  };

  const handleSelectTemplate = async (t) => {
    try {
      const res = await fetch(`${API_URL}/api/magic/templates/${t.template_id}`);
      const data = await res.json();
      if (data.image_b64) {
        setResult({ image_base64: data.image_b64, package: data.package, occasion: data.occasion, is_template: true });
        setRevealPhase("revealed");
        setSelectedPkg(data.package);
        setSelectedOccasion(data.occasion);
      }
    } catch (e) { console.debug('[Magic] Template load failed:', e.message); }
  };

  return (
    <div className="min-h-screen bg-[#FFFAF5] relative overflow-hidden">
      <MagicLogoWatermark />
      <EarlyBirdStrip />
      <MagicHeader />

      <div className="relative z-10 max-w-5xl mx-auto px-4 py-6 sm:py-8">
        {/* ⚡ Dedup cache hit notice — slides down from the top, fades after 4s */}
        {dedupNotice && (
          <div
            data-testid="magic-dedup-notice"
            className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-sm font-semibold shadow-2xl flex items-center gap-2 animate-[slideDown_0.3s_ease-out]"
            style={{
              animation: "magicDedupSlide 0.3s ease-out",
            }}
          >
            <span className="text-base">✨</span>
            <span>Reused from a recent generation — instant!</span>
          </div>
        )}
        <style>{`
          @keyframes magicDedupSlide {
            0% { transform: translate(-50%, -32px); opacity: 0; }
            100% { transform: translate(-50%, 0); opacity: 1; }
          }
        `}</style>

        {/* Reveal Animation */}
        {result && revealPhase && revealPhase !== "revealed" && (
          <MagicRevealAnimation revealPhase={revealPhase} />
        )}

        {/* Result View */}
        {result && revealPhase === "revealed" && (
          <MagicResultView
            result={result}
            uploadedImage={uploadedImage}
            sliderPos={sliderPos}
            sliderContainerRef={sliderContainerRef}
            handleSliderMouseDown={handleSliderMouseDown}
            handleSliderTouchStart={handleSliderTouchStart}
            handleDownload={handleDownload}
            handleShare={handleShare}
            onReset={() => { setResult(null); setSliderPos(50); setRevealPhase(null); toggleTemplates(true); }}
            packages={PACKAGES}
          />
        )}

        <div className={`grid grid-cols-1 ${(result && revealPhase === 'revealed') ? 'lg:grid-cols-1' : 'lg:grid-cols-2'} gap-6`}>
          {/* Template Gallery */}
          {!result && !revealPhase && (
            <MagicTemplateGallery
              templates={templates}
              showTemplates={showTemplates}
              setShowTemplates={toggleTemplates}
              onSelectTemplate={handleSelectTemplate}
            />
          )}

          {/* "Show previews" chip — appears whenever the user has hidden the
              template gallery, so they always have a way to bring it back. */}
          {!result && !revealPhase && !showTemplates && templates.length > 0 && (
            <div className="lg:col-span-2 -mb-2" data-testid="magic-unhide-wrap">
              <button
                type="button"
                onClick={() => toggleTemplates(true)}
                data-testid="magic-unhide-templates-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#E83E8C]/10 to-[#FF9F1C]/10 hover:from-[#E83E8C]/15 hover:to-[#FF9F1C]/15 border border-[#E83E8C]/20 text-[#E83E8C] text-xs font-semibold transition-all hover:scale-[1.02]"
              >
                <Sparkles size={14} /> Show AI-generated previews ({templates.length})
              </button>
              <p className="text-[11px] text-[#94a3b8] mt-1.5">You hid the AI preview gallery — tap above to bring it back.</p>
            </div>
          )}

          {/* Upload Area */}
          {!result && !revealPhase && (
            <MagicUploader
              uploadedImage={uploadedImage}
              fileRef={fileRef}
              handleFileSelect={handleFileSelect}
              setUploadedFile={setUploadedFile}
              setUploadedImage={setUploadedImage}
              setResult={setResult}
              setError={setError}
              handleReset={handleReset}
            />
          )}

          {/* Customization Panel */}
          {!result && !revealPhase && (
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-[#1E1B4B]">2. Customize Your Decoration</h2>

              {/* Package */}
              <div>
                <label className="text-xs font-semibold text-[#475569] uppercase tracking-wider mb-2 block">Package Style</label>
                <div className="grid grid-cols-2 gap-2" data-testid="magic-package-selector">
                  {PACKAGES.map(pkg => (
                    <button key={pkg.name} onClick={() => setSelectedPkg(pkg.name)}
                      className={`relative text-left p-3 rounded-xl border-2 transition-all ${selectedPkg === pkg.name ? 'border-[#E83E8C] bg-white shadow-sm' : 'border-gray-200 bg-white/60 hover:border-[#E83E8C]/30'}`}
                      data-testid={`magic-pkg-${pkg.name.toLowerCase()}`}>
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${pkg.gradient} mb-1.5`} />
                      <div className="flex items-baseline gap-1.5 flex-wrap">
                        <p className="text-xs font-bold text-[#1E1B4B]">{pkg.name}</p>
                        <p className="text-[10px] font-semibold text-[#E83E8C]" data-testid={`magic-pkg-price-${pkg.name.toLowerCase()}`}>
                          {pkg.name === "Custom" ? "Build Your Party" : `₹${pkg.price} onwards`}
                        </p>
                      </div>
                      <p className="text-[10px] text-[#94a3b8] leading-tight mt-0.5">{pkg.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Add-On Picker (only when Custom is selected) */}
              {selectedPkg === "Custom" && (
                <MagicCustomAddons customItems={customItems} setCustomItems={setCustomItems} />
              )}

              {/* Occasion */}
              <div>
                <label className="text-xs font-semibold text-[#475569] uppercase tracking-wider mb-2 block">Occasion</label>
                <div className="flex flex-wrap gap-1.5" data-testid="magic-occasion-selector">
                  {OCCASIONS.map(occ => (
                    <button key={occ} onClick={() => setSelectedOccasion(occ)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${selectedOccasion === occ ? 'bg-[#E83E8C] text-white border-[#E83E8C]' : 'border-gray-200 text-[#475569] hover:border-[#E83E8C]/40 bg-white'}`}
                      data-testid={`magic-occ-${occ.toLowerCase().replace(/\s+/g, '-')}`}>
                      {occ}
                    </button>
                  ))}
                </div>
              </div>

              {/* Colors */}
              <div>
                <label className="text-xs font-semibold text-[#475569] uppercase tracking-wider mb-2 flex items-center gap-1.5 block">
                  <Palette size={13} /> Color Scheme
                </label>
                <div className="grid grid-cols-2 gap-1.5 mb-2" data-testid="magic-color-presets">
                  {COLOR_PRESETS.map(preset => (
                    <button key={preset.name} onClick={() => { setSelectedColors(preset.value); setCustomColors(""); }}
                      className={`px-2.5 py-1.5 rounded-lg text-xs border transition-all text-left ${selectedColors === preset.value ? 'border-[#E83E8C] bg-[#E83E8C]/5 text-[#1E1B4B] font-medium' : 'border-gray-200 text-[#475569] hover:border-[#E83E8C]/30 bg-white'}`}>
                      {preset.name}
                    </button>
                  ))}
                </div>
                <input value={customColors} onChange={(e) => { setCustomColors(e.target.value); setSelectedColors(""); }}
                  placeholder="Or type your own: e.g. baby pink, white, silver"
                  className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 focus:border-[#E83E8C] outline-none bg-white" data-testid="magic-custom-colors" />
              </div>

              {/* Extras */}
              <div>
                <label className="text-xs font-semibold text-[#475569] uppercase tracking-wider mb-1.5 block">Extra Elements (optional)</label>
                <input value={extras} onChange={(e) => setExtras(e.target.value)}
                  placeholder="e.g. number 25 balloon, photo booth corner, candy table"
                  className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 focus:border-[#E83E8C] outline-none bg-white" data-testid="magic-extras-input" />
              </div>

              {/* Usage & Team */}
              <div className="space-y-2">
                {teamMode ? (
                  <div className="flex items-center gap-2 bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-200" data-testid="magic-team-active">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-[11px] text-emerald-700 font-medium">Team Test Mode — Unlimited generations</span>
                    <button onClick={() => { setTeamMode(false); setTeamCode(""); }} className="ml-auto text-[10px] text-emerald-600 hover:underline">Exit</button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-[#1E1B4B]/5 rounded-lg px-3 py-2" data-testid="magic-usage-info">
                    <span className="text-[11px] text-[#475569]">
                      {usage.free_remaining > 0 ? <>{usage.free_remaining} free preview{usage.free_remaining > 1 ? 's' : ''} left</>
                        : usage.has_pass ? <span className="text-emerald-600 font-medium flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" /> {usage.pass_name || 'Magic Pass'} Active</span>
                        : <span className="text-amber-600 font-medium">Free previews used</span>}
                    </span>
                    <div className="flex items-center gap-2">
                      {!usage.has_pass && usage.free_remaining <= 0 && (
                        <button type="button" onClick={() => setShowPaywall(true)} className="text-[11px] text-[#E83E8C] font-semibold hover:underline">Get Magic Pass</button>
                      )}
                      <button type="button" onClick={() => setShowTeamInput(!showTeamInput)} className="text-[10px] text-[#94a3b8] hover:text-[#475569]" data-testid="team-access-toggle">Team</button>
                    </div>
                  </div>
                )}
                {showTeamInput && !teamMode && (
                  <div className="flex gap-2">
                    <input type="password" value={teamCode} onChange={(e) => setTeamCode(e.target.value)} placeholder="Enter team code"
                      className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-gray-200 focus:border-emerald-500 outline-none" data-testid="team-code-input" />
                    <button type="button"
                      onClick={() => { if (teamCode.trim()) { setTeamMode(true); sessionStorage.setItem("dg_team", "1"); sessionStorage.setItem("dg_team_code", teamCode); setShowTeamInput(false); } }}
                      className="px-3 py-1.5 bg-emerald-500 text-white text-xs rounded-lg font-medium hover:bg-emerald-600" data-testid="team-code-submit">Activate</button>
                  </div>
                )}
              </div>

              {/* Generate Button */}
              {error && <p className="text-xs text-red-500 font-medium" data-testid="magic-error">{error}</p>}
              <button onClick={handleGenerate} disabled={!uploadedFile || generating || (selectedPkg === "Custom" && customItems.size === 0)}
                className={`w-full py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all ${!uploadedFile || (selectedPkg === "Custom" && customItems.size === 0) ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : generating ? 'bg-[#E83E8C]/80 text-white cursor-wait' : 'bg-gradient-to-r from-[#E83E8C] to-purple-600 text-white hover:shadow-lg hover:shadow-[#E83E8C]/25 active:scale-[0.98]'}`}
                data-testid="magic-generate-btn">
                {generating ? (<><Loader2 size={18} className="animate-spin" /><span>Creating your magic decoration...</span></>)
                  : (<><Sparkles size={18} /><span>{teamMode ? 'Generate (Team Test)' : selectedPkg === "Custom" ? `Generate Custom (Rs ${ADDON_CATEGORIES.flatMap(c => c.items).filter(a => customItems.has(a.id)).reduce((s, a) => s + a.price, 0).toLocaleString()})` : 'Generate DoozyGo® Magic'}</span></>)}
              </button>
              {generating && (
                <div className="text-center space-y-2">
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#E83E8C] animate-bounce" style={{ animationDelay: '0s' }} />
                    <div className="w-2 h-2 rounded-full bg-[#E83E8C] animate-bounce" style={{ animationDelay: '0.2s' }} />
                    <div className="w-2 h-2 rounded-full bg-[#E83E8C] animate-bounce" style={{ animationDelay: '0.4s' }} />
                  </div>
                  <p className="text-xs text-[#94a3b8]" data-testid="magic-progress-text">{genStatus || "AI is designing your party setup... this takes 45-90 seconds"}</p>
                  <p className="text-[10px] text-[#cbd5e1]">Please wait — clicking generate again will not speed it up.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* How It Works */}
        {!result && !revealPhase && (
          <div className="mt-12 mb-4">
            <div className="text-center mb-6">
              <h3 className="font-['Outfit'] text-lg font-bold text-[#1E1B4B]">How It Works</h3>
              <div className="w-10 h-0.5 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] mx-auto mt-2" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-3xl mx-auto">
              {[
                { icon: Camera, title: "Upload Photo", desc: "Take a photo of your empty room or party space", color: "from-[#E83E8C] to-rose-500", num: "1" },
                { icon: Wand2, title: "Customize Style", desc: "Pick your package, occasion & color scheme", color: "from-[#FF9F1C] to-amber-500", num: "2" },
                { icon: Sparkles, title: "See The Magic", desc: "AI decorates your room — see before vs after", color: "from-violet-500 to-purple-600", num: "3" },
              ].map((step) => (
                <div key={step.num} className="relative bg-white rounded-2xl p-5 border border-[#f0e6d9] shadow-sm hover:shadow-md transition-shadow text-center group">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className={`w-6 h-6 rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center text-white text-[10px] font-bold`}>{step.num}</span>
                  </div>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${step.color} flex items-center justify-center mx-auto mb-3 shadow-sm group-hover:scale-110 transition-transform`}>
                    <step.icon size={22} className="text-white" />
                  </div>
                  <p className="text-sm font-bold text-[#1E1B4B] mb-1">{step.title}</p>
                  <p className="text-xs text-[#94a3b8] leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 text-center bg-gradient-to-r from-[#1E1B4B] via-[#312e81] to-[#1E1B4B] rounded-2xl p-6 sm:p-8 relative overflow-hidden">
              <div className="absolute top-3 right-[20%] w-12 h-12 bg-[#E83E8C]/20 rounded-full blur-lg animate-float" />
              <div className="absolute bottom-3 left-[15%] w-10 h-10 bg-[#FFD166]/20 rounded-full blur-lg animate-float" style={{ animationDelay: '1s' }} />
              <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Ready to host?</p>
              <h3 className="text-white text-xl sm:text-2xl font-bold mb-2">Love what you see? Book the real thing!</h3>
              <p className="text-white/50 text-sm mb-5 max-w-md mx-auto">DoozyGo sets up your actual party just like the AI preview — starting at just Rs 3,999</p>
              <a href="/#book-online" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white px-8 py-3.5 rounded-full font-bold text-sm hover:scale-105 transition-transform shadow-lg" data-testid="magic-book-cta">
                Book Your Party Now
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowLoginPrompt(false)} data-testid="magic-login-prompt">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 bg-[#E83E8C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <LogIn size={28} className="text-[#E83E8C]" />
            </div>
            <h3 className="text-xl font-bold text-[#1E1B4B] mb-2">Sign in to Generate</h3>
            <p className="text-sm text-[#475569] mb-5">Please sign in to your DoozyGo account to generate AI decoration previews. Your images will be saved to your account!</p>
            <button
              onClick={() => { setShowLoginPrompt(false); localStorage.setItem("doozygo_return_url", "/doozygo-magic"); navigate('/my-account'); }}
              className="w-full py-3 rounded-xl bg-[#E83E8C] text-white font-semibold hover:bg-[#d1356f] transition-colors mb-2"
              data-testid="magic-login-btn"
            >
              Sign In / Create Account
            </button>
            <button onClick={() => setShowLoginPrompt(false)} className="text-sm text-[#94a3b8] hover:text-[#475569]">Cancel</button>
          </div>
        </div>
      )}

      {/* Paywall Modal */}
      <MagicPaywall
        showPaywall={showPaywall} setShowPaywall={setShowPaywall}
        buyingPlan={buyingPlan} setBuyingPlan={setBuyingPlan}
        setUsage={setUsage} setError={setError}
        paywallTeamOpen={paywallTeamOpen} setPaywallTeamOpen={setPaywallTeamOpen}
        paywallTeamCode={paywallTeamCode} setPaywallTeamCode={setPaywallTeamCode}
        setTeamMode={setTeamMode} setTeamCode={setTeamCode}
      />
    </div>
  );
}
